import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

// Mock data for charts
const revenueData = [
  { month: 'Jan', amount: 4000 },
  { month: 'Feb', amount: 3000 },
  { month: 'Mar', amount: 5000 },
  { month: 'Apr', amount: 4500 },
  { month: 'May', amount: 6000 },
  { month: 'Jun', amount: 5500 },
];

const reservationsData = [
  { month: 'Jan', count: 240 },
  { month: 'Feb', count: 300 },
  { month: 'Mar', count: 320 },
  { month: 'Apr', amount: 380 },
  { month: 'May', count: 400 },
  { month: 'Jun', count: 450 },
];

const ReportsPage = () => {
  const [loading] = useState(false);
  const [error] = useState(null);
  const [timeRange, setTimeRange] = useState('6months');

  const handleRangeChange = (event) => {
    setTimeRange(event.target.value);
    // In a real app, you would fetch new data based on the selected range
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Reports & Analytics
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            id="time-range-select"
            value={timeRange}
            label="Time Range"
            onChange={handleRangeChange}
          >
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="90days">Last 90 Days</MenuItem>
            <MenuItem value="6months">Last 6 Months</MenuItem>
            <MenuItem value="1year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Revenue Summary Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardHeader title="Total Revenue" />
            <Divider />
            <CardContent>
              <Typography variant="h3" component="div" align="center">
                $28,500
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" align="center">
                +12% from previous period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Reservations Summary Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardHeader title="Total Reservations" />
            <Divider />
            <CardContent>
              <Typography variant="h3" component="div" align="center">
                2,090
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" align="center">
                +8% from previous period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Party Size Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardHeader title="Avg. Party Size" />
            <Divider />
            <CardContent>
              <Typography variant="h3" component="div" align="center">
                3.7
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" align="center">
                +0.2 from previous period
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Revenue Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={revenueData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#8884d8" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Reservations Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Reservation Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={reservationsData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Export Reports Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Export Reports
            </Typography>
            <Typography variant="body2" paragraph>
              Download detailed reports for your records or further analysis.
            </Typography>
            <Box display="flex" gap={2}>
              <Button variant="outlined">Revenue Report (CSV)</Button>
              <Button variant="outlined">Reservations Report (CSV)</Button>
              <Button variant="outlined">Customer Report (CSV)</Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReportsPage; 